/**
 * Enums de negócio compartilhados entre `server` e `web`.
 *
 * Os valores são os mesmos gravados no PostgreSQL (via Prisma). Alterar um valor
 * aqui exige migration — não é renomeação livre.
 */
export const UserRole = {
    WELOZE_ADMIN: 'WELOZE_ADMIN',
    WELOZE_VIEWER: 'WELOZE_VIEWER',
    SUPPLIER_USER: 'SUPPLIER_USER',
};
/** Quem enxerga todas as lojas sem precisar de linha em `user_store_access`. */
export const WELOZE_ROLES = [UserRole.WELOZE_ADMIN, UserRole.WELOZE_VIEWER];
export function isWelozeRole(role) {
    return WELOZE_ROLES.includes(role);
}
export const UserStatus = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    ANONYMIZED: 'ANONYMIZED',
};
/**
 * O papel, em português.
 *
 * "Consulta" e não "Leitura": quem usa a tela é a Qualidade, e o que a pessoa
 * precisa entender é o que ela **pode fazer**, não como o guard foi escrito.
 */
export const USER_ROLE_LABEL = {
    WELOZE_ADMIN: 'Administrador Weloze',
    WELOZE_VIEWER: 'Consulta Weloze',
    SUPPLIER_USER: 'Fornecedor',
};
export const USER_ROLE_HINT = {
    WELOZE_ADMIN: 'Enxerga a carteira inteira e altera: cadastro, RNC, certificado, fechamento e usuários.',
    WELOZE_VIEWER: 'Enxerga a carteira inteira e não altera nada.',
    SUPPLIER_USER: 'Enxerga apenas as lojas concedidas, uma a uma.',
};
export const USER_STATUS_LABEL = {
    ACTIVE: 'Ativo',
    INACTIVE: 'Desativado',
    ANONYMIZED: 'Anonimizado',
};
export const EntityStatus = {
    ACTIVE: 'ACTIVE',
    BLOCKED: 'BLOCKED',
    INACTIVE: 'INACTIVE',
};
export const SupplierKind = {
    MATERIAL: 'MATERIAL',
    SERVICE: 'SERVICE',
    SPECIAL_PROCESS: 'SPECIAL_PROCESS',
    EXTERNAL_LAB: 'EXTERNAL_LAB',
};
export const HomologationStatus = {
    APPROVED: 'APPROVED',
    CONDITIONAL: 'CONDITIONAL',
    UNSATISFACTORY: 'UNSATISFACTORY',
    NOT_EVALUATED: 'NOT_EVALUATED',
};
/**
 * Classificação do IQW. `NOT_APPLICABLE` é a competência sem item de pedido
 * encerrado — não é nota zero, é ausência de avaliação (PLAN §3.1).
 */
export const Classification = {
    EXCELLENT: 'EXCELLENT',
    GOOD: 'GOOD',
    UNSATISFACTORY: 'UNSATISFACTORY',
    NOT_APPLICABLE: 'NOT_APPLICABLE',
};
export const CLASSIFICATION_LABEL = {
    EXCELLENT: 'Excelente',
    GOOD: 'Bom',
    UNSATISFACTORY: 'Insatisfatório',
    NOT_APPLICABLE: 'Não aplicável',
};
/**
 * Documentos da ficha do fornecedor. **Nenhum deles entra em índice** — têm
 * validade e alerta próprios. O certificado do SGQ não está aqui de propósito:
 * ele é `Certification` e é o único que mexe no ISQ.
 */
export const SupplierDocKind = {
    OPERATING_LICENSE: 'OPERATING_LICENSE',
    SELF_ASSESSMENT: 'SELF_ASSESSMENT',
    SPECIAL_PROCESS_CQI: 'SPECIAL_PROCESS_CQI',
    MANUAL_ACCEPTANCE: 'MANUAL_ACCEPTANCE',
    OTHER: 'OTHER',
};
export const SUPPLIER_DOC_KIND_LABEL = {
    OPERATING_LICENSE: 'Licença de Operação',
    SELF_ASSESSMENT: 'Auto-avaliação (RQ 093)',
    SPECIAL_PROCESS_CQI: 'Processo especial (CQI)',
    MANUAL_ACCEPTANCE: 'Termo de aceite do manual',
    OTHER: 'Outro',
};
export const AuditKind = {
    SELF_ASSESSMENT: 'SELF_ASSESSMENT',
    SECOND_PARTY: 'SECOND_PARTY',
};
export const AUDIT_KIND_LABEL = {
    SELF_ASSESSMENT: 'Auto-avaliação',
    SECOND_PARTY: 'Auditoria de segunda parte',
};
export const AuditStatus = {
    PLANNED: 'PLANNED',
    DONE: 'DONE',
    CANCELLED: 'CANCELLED',
};
export const AUDIT_STATUS_LABEL = {
    PLANNED: 'Prevista',
    DONE: 'Realizada',
    CANCELLED: 'Cancelada',
};
export const ActionPlanOrigin = {
    SEMESTER_UNSATISFACTORY: 'SEMESTER_UNSATISFACTORY',
    RNC: 'RNC',
    AUDIT: 'AUDIT',
    OTHER: 'OTHER',
};
export const ACTION_PLAN_ORIGIN_LABEL = {
    SEMESTER_UNSATISFACTORY: 'Semestre insatisfatório',
    RNC: 'RNC',
    AUDIT: 'Auditoria',
    OTHER: 'Outro',
};
/**
 * O tipo da ação do D5.
 *
 * Mora aqui, e não no `eightd.ts`, porque é uma coluna de `ActionPlan` — a
 * mesma entidade do plano semestral. O 8D não guarda ações próprias: ele mostra
 * as que existem, com o mesmo acompanhamento de prazo.
 */
export const ActionType = {
    CONTAINMENT: 'CONTAINMENT',
    SCOPE: 'SCOPE',
    DETECTION: 'DETECTION',
    CORRECTIVE: 'CORRECTIVE',
};
export const ACTION_TYPE_LABEL = {
    CONTAINMENT: 'Contenção',
    SCOPE: 'Abrangência',
    DETECTION: 'Detecção',
    CORRECTIVE: 'Corretiva',
};
export const ActionPlanStatus = {
    OPEN: 'OPEN',
    IN_PROGRESS: 'IN_PROGRESS',
    DONE: 'DONE',
    OVERDUE: 'OVERDUE',
    CANCELLED: 'CANCELLED',
};
export const ACTION_PLAN_STATUS_LABEL = {
    OPEN: 'Aberto',
    IN_PROGRESS: 'Em andamento',
    DONE: 'Concluído',
    OVERDUE: 'Atrasado',
    CANCELLED: 'Cancelado',
};
export const ENTITY_STATUS_LABEL = {
    ACTIVE: 'Ativo',
    BLOCKED: 'Bloqueado',
    INACTIVE: 'Inativo',
};
export const SUPPLIER_KIND_LABEL = {
    MATERIAL: 'Material / Componente',
    SERVICE: 'Serviços',
    SPECIAL_PROCESS: 'Processo especial',
    EXTERNAL_LAB: 'Laboratório externo',
};
export const HOMOLOGATION_STATUS_LABEL = {
    APPROVED: 'Aprovado',
    CONDITIONAL: 'Aprovado com ressalva',
    UNSATISFACTORY: 'Insatisfatório',
    NOT_EVALUATED: 'Não avaliado',
};
// --- Certificações (fatia c) -----------------------------------------------
export const CertStandard = {
    ISO_9001: 'ISO_9001',
    ISO_14001: 'ISO_14001',
    IATF_16949: 'IATF_16949',
    ISO_IEC_17025: 'ISO_IEC_17025',
    OTHER: 'OTHER',
};
export const CERT_STANDARD_LABEL = {
    ISO_9001: 'ISO 9001',
    ISO_14001: 'ISO 14001',
    IATF_16949: 'IATF 16949',
    ISO_IEC_17025: 'ISO/IEC 17025',
    OTHER: 'Outra norma',
};
export const CertStatus = {
    VALID: 'VALID',
    EXPIRED: 'EXPIRED',
    SUSPENDED: 'SUSPENDED',
    CANCELLED: 'CANCELLED',
};
export const CERT_STATUS_LABEL = {
    VALID: 'Vigente',
    EXPIRED: 'Vencido',
    SUSPENDED: 'Suspenso',
    CANCELLED: 'Cancelado',
};
export const CertReview = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};
export const CERT_REVIEW_LABEL = {
    PENDING: 'Aguardando validação',
    APPROVED: 'Validado',
    REJECTED: 'Recusado',
};
/**
 * Situação de certificação que gera o demérito do ISQ.
 *
 * Não é a norma: é a **combinação** de normas vigentes. "ISO 9001 + ISO 14001"
 * é um estado do fornecedor, e é por isso que o demérito não pode morar no
 * registro de um certificado.
 */
export const CertificationTier = {
    IATF: 'IATF',
    ISO9001_WITH_IATF_ROADMAP: 'ISO9001_WITH_IATF_ROADMAP',
    ISO9001_AND_ISO14001: 'ISO9001_AND_ISO14001',
    ISO9001: 'ISO9001',
    NONE: 'NONE',
};
export const CERTIFICATION_TIER_LABEL = {
    IATF: 'IATF 16949 vigente',
    ISO9001_WITH_IATF_ROADMAP: 'ISO 9001 com cronograma IATF válido',
    ISO9001_AND_ISO14001: 'ISO 9001 e ISO 14001 vigentes',
    ISO9001: 'ISO 9001 vigente',
    NONE: 'Sem certificação vigente',
};
// --- RNC (fatia c) ---------------------------------------------------------
export const RncSeverity = {
    MINOR: 'MINOR',
    MEDIUM: 'MEDIUM',
    MAJOR: 'MAJOR',
};
export const RNC_SEVERITY_LABEL = {
    MINOR: 'Menor',
    MEDIUM: 'Média',
    MAJOR: 'Maior',
};
export const RncStatus = {
    OPEN: 'OPEN',
    AWAITING_SUPPLIER: 'AWAITING_SUPPLIER',
    IN_ANALYSIS: 'IN_ANALYSIS',
    ACTIONS_IMPLEMENTED: 'ACTIONS_IMPLEMENTED',
    CLOSED: 'CLOSED',
    CANCELLED: 'CANCELLED',
};
export const RNC_STATUS_LABEL = {
    OPEN: 'Aberta',
    AWAITING_SUPPLIER: 'Aguardando fornecedor',
    IN_ANALYSIS: 'Em análise',
    ACTIONS_IMPLEMENTED: 'Ações implementadas',
    CLOSED: 'Encerrada',
    CANCELLED: 'Cancelada',
};
/** Etapas do 8D, na ordem do RQ 230-01 §6.1. */
export const RNC_STAGES = [
    { key: 'containment', label: 'Contenção', deadline: '24 horas' },
    { key: 'supplierResponse', label: 'Retorno do 8D', deadline: '15 dias' },
    { key: 'analysis', label: 'Análise realizada', deadline: null },
    { key: 'actionsImplemented', label: 'Evidências das ações', deadline: '60 dias' },
    { key: 'effectiveness', label: 'Avaliação da eficácia', deadline: '90 dias' },
];
// --- Competência e avaliação (fatia d) -------------------------------------
/**
 * Estado da competência. `CLOSED` congela os números; `PUBLISHED` é o momento
 * em que o fornecedor passa a enxergá-los. Reabrir só existe entre os dois.
 */
export const PeriodStatus = {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
    PUBLISHED: 'PUBLISHED',
};
export const PERIOD_STATUS_LABEL = {
    OPEN: 'Aberta',
    CLOSED: 'Fechada',
    PUBLISHED: 'Publicada',
};
export const DeliveryDateSource = {
    ZDTENTR: 'ZDTENTR',
    DTDIGIT: 'DTDIGIT',
};
/**
 * De onde saiu a data de entrega. O fornecedor precisa poder conferir: quando a
 * digitação foi colada na entrega, o campo próprio fica vazio e vale a data de
 * digitação da nota.
 */
export const DELIVERY_DATE_SOURCE_LABEL = {
    ZDTENTR: 'Data de entrega informada na nota',
    DTDIGIT: 'Data de digitação da nota (campo de entrega vazio)',
};
/**
 * Por que um item ficou fora do IDC. Todo item excluído carrega um destes —
 * o fornecedor precisa ver o motivo, não só a ausência (CLAUDE.md).
 */
export const IdcExclusionReason = {
    NO_PURCHASE_ORDER: 'NO_PURCHASE_ORDER',
    TES_OUT_OF_SCOPE: 'TES_OUT_OF_SCOPE',
    RETURN: 'RETURN',
    NOT_MONITORED: 'NOT_MONITORED',
    STILL_OPEN: 'STILL_OPEN',
    CLOSED_WITHOUT_DELIVERY: 'CLOSED_WITHOUT_DELIVERY',
    BEFORE_MONITORED_FROM: 'BEFORE_MONITORED_FROM',
    OTHER_BRANCH: 'OTHER_BRANCH',
};
export const IDC_EXCLUSION_LABEL = {
    NO_PURCHASE_ORDER: 'Entrega sem pedido de compra vinculado',
    TES_OUT_OF_SCOPE: 'TES fora do escopo da avaliação',
    RETURN: 'Devolução',
    NOT_MONITORED: 'Loja não monitorada',
    STILL_OPEN: 'Item de pedido ainda em aberto',
    CLOSED_WITHOUT_DELIVERY: 'Pedido encerrado sem entrega registrada',
    BEFORE_MONITORED_FROM: 'Anterior ao início do monitoramento',
    OTHER_BRANCH: 'Filial fora do escopo',
};
// --- Importação do Protheus (fatia e) --------------------------------------
export const ImportStatus = {
    RUNNING: 'RUNNING',
    SUCCESS: 'SUCCESS',
    PARTIAL: 'PARTIAL',
    FAILED: 'FAILED',
};
export const IMPORT_STATUS_LABEL = {
    RUNNING: 'Em execução',
    SUCCESS: 'Concluída',
    PARTIAL: 'Concluída com ressalvas',
    FAILED: 'Falhou',
};
export const ImportTrigger = {
    CRON: 'CRON',
    MANUAL: 'MANUAL',
};
export const IMPORT_TRIGGER_LABEL = {
    CRON: 'Automática',
    MANUAL: 'Manual',
};
//# sourceMappingURL=enums.js.map